<template>
  <div class="about">
    <page-header :heading="'Your Account'" />
    <div class="wrapper">
      <div
        class="max-w-2xl mx-auto px-8 py-8 -mt-12 bg-white rounded-lg shadow-md"
        v-if="user"
      >
        <div
          v-if="
            user.attributes['custom:stripe_id'] &&
              user.attributes['custom:stripe_sub_id']
          "
        >
          <h2 class="mb-4 text-lg text-center border-b-2 pb-3 border-gray-100">
            Your Subscription
          </h2>
          <p v-if="plan" class="text-center">
            <strong>Current plan:</strong>
            {{ user.attributes["custom:stripe_plan_name"] }} - ${{
              plan.amount / 100
            }}
            per
            {{ plan.interval }}
          </p>
          <div class="text-center py-0" v-else>
            <svg
              class="inline-block animate-spin -ml-1 mr-1 h-5 w-5 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Loading
          </div>
          <button class="btn mx-auto block mt-4" @click="manageSubscription">
            <span v-if="!loadingPortal">Manage Subscription</span>
            <svg
              v-else
              class="inline-block animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </button>
        </div>
        <div v-else>
          <h2 class="mb-4 text-lg text-center border-b-2 pb-3 border-gray-100">
            Upgrade Now
          </h2>
          <label for="postcode">Step 1. Choose your country</label>
          <select v-model="user.attributes['custom:country']">
            <option disabled selected>Please select your country</option>
            <option v-for="country in countries" :key="country.alpha2code">{{
              country.name
            }}</option>
          </select>
          <label class="mt-4" for="postcode">Step 2. Choose your plan</label>
          <select v-model="priceId">
            <option disabled selected value="">Select a plan to upgrade</option>
            <option
              v-for="price in prices"
              :key="price.priceId"
              :value="price.priceId"
            >
              {{ price.name }} - &dollar; {{ price.amount / 100 }} per
              {{ price.interval }}
            </option>
          </select>
          <button
            class="btn mx-auto block mt-4"
            @click="checkout"
            :disabled="checkoutDisabled"
          >
            <span v-if="!loadingCheckout">Upgrade Subscription</span>
            <svg
              v-else
              class="inline-block animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </button>
          <p class="text-center mt-3 text-sm" v-if="eu && euBlocked">
            {{ euBlocked }}
          </p>
        </div>
      </div>
    </div>
    <div
      class="max-w-2xl mx-auto px-8 py-8 mt-6 bg-white rounded-lg shadow-md"
      v-if="user"
    >
      <h2 class="mb-4 text-lg text-center border-b-2 pb-3 border-gray-100">
        Your Profile
      </h2>
      <label for="email">Email</label>
      <input
        name="email"
        class="w-full mb-4"
        v-model="user.attributes.email"
        type="text"
        placeholder="Your name"
      />
      <label for="name">Name</label>
      <input
        name="name"
        class="w-full mb-4"
        v-model="user.attributes.name"
        type="text"
        placeholder="Your name"
      />
      <hr class="border-b border-gray-100 my-4" />
      <label for="company">Company</label>
      <input
        name="company"
        class="w-full mb-4"
        v-model="user.attributes['custom:company']"
        type="text"
        placeholder="Your company name"
      />
      <label for="vat">VAT Number</label>
      <input
        name="vat"
        class="w-full md:w-1/2 mb-4"
        v-model="user.attributes['custom:vat_number']"
        type="text"
        placeholder="VAT Registration Number"
      />
      <hr class="border-b border-gray-100 my-4" />
      <label for="address_1">Address</label>
      <input
        name="address_1"
        class="w-full"
        v-model="user.attributes['custom:address_1']"
        type="text"
        placeholder="Address line one"
      />
      <input
        name="address_2"
        class="w-full mb-4"
        v-model="user.attributes['custom:address_2']"
        type="text"
        placeholder="Address line two"
      />
      <label for="city">Town / City</label>
      <input
        name="city"
        class="w-full mb-4"
        v-model="user.attributes['custom:city']"
        type="text"
        placeholder="Town / City"
      />
      <label for="state">State / County</label>
      <input
        name="state"
        class="w-full mb-4"
        v-model="user.attributes['custom:state']"
        type="text"
        placeholder="State / County"
      />
      <label for="postcode">Post / Zip Code</label>
      <input
        name="postcode"
        class="w-full md:w-1/2 mb-4"
        v-model="user.attributes['custom:postcode']"
        type="text"
        placeholder="Post / Zip Code"
      />
      <label for="postcode">Country</label>
      <select v-model="user.attributes['custom:country']">
        <option disabled value="">Please select one</option>
        <option v-for="country in countries" :key="country.alpha2">{{
          country.name
        }}</option>
      </select>
      <button class="btn mx-auto block mt-4" @click="updateProfile">
        <span v-if="!saving">Save</span>
        <svg
          v-else
          class="inline-block animate-spin h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script type="text/javascript">
import { Auth, API, Analytics } from "aws-amplify";
import PageHeader from "@/components/PageHeader";
import { stripe_pricing as prices } from "@/config/stripe.js";
import countries from "@/util/countries.json";
import eu from "@/util/eu.json";

export default {
  name: "Account",
  components: {
    PageHeader,
  },
  data() {
    return {
      loadingPortal: false,
      loadingCheckout: false,
      saving: false,
      user: null,
      countries: null,
      eu: null,
      priceId: "",
      prices: null,
      plan: null,
      planName: null,
    };
  },
  methods: {
    async updateProfile() {
      this.saving = true;
      const result = await Auth.updateUserAttributes(
        this.user,
        this.user.attributes
      );
      this.saving = false;
      if (result == "SUCCESS") {
        alert("Your profile has been updated");
        Analytics.updateEndpoint({
          address: this.user.attributes.email,
          channelType: "EMAIL",
          optOut: "NONE",
          userId: this.user.attributes.sub,
          userAttributes: {
            username: [this.user.username],
            company: [this.user.attributes["custom:company"]],
            name: [this.user.attributes.name],
            country: [this.user.attributes["custom:country"]],
          },
        });
      } else {
        alert("Something has gone wrong. Check your details and try again");
      }
    },
    async checkout() {
      this.loadingCheckout = true;
      const checkoutInit = {
        body: {
          priceId: this.priceId,
          customer: this.user.attributes["custom:stripe_id"],
          customer_email: this.user.attributes.email,
          website: window.location.href,
        },
      };
      let response = await API.post(
        "stripe",
        "/create-checkout-session",
        checkoutInit
      );
      window.location.href = response.session.url;
    },
    async manageSubscription() {
      this.loadingPortal = true;
      let response = await API.get("stripe", "/portal");
      window.location.href = response.result.url;
    },
  },
  computed: {
    checkoutDisabled: {
      cache: false,
      get() {
        if (!this.user.attributes["custom:country"]) return true;
        if (!this.priceId) return true;
        if (this.euBlocked) return true;
        return false;
      },
    },
    euBlocked: {
      cache: false,
      get() {
        if (
          !this.eu ||
          this.user.attributes["custom:country"] ==
            "United Kingdom of Great Britain and Northern Ireland"
        ) {
          return null;
        }
        let response;
        this.eu.forEach((country) => {
          if (country.name == this.user.attributes["custom:country"]) {
            console.log("country in the EU");
            response =
              "Sorry, ScreenLab is not currently available to customers in the EU.";
          }
        });
        return response;
      },
    },
  },
  async created() {
    let user = await Auth.currentAuthenticatedUser({ bypassCache: true });
    this.user = user;
    this.prices = prices;
    this.countries = countries;
    this.eu = eu;
    Analytics.record({
      name: "viewedAccount",
      attributes: {
        username: user.username,
      },
    });
    if (user.attributes["custom:stripe_sub_id"]) {
      const checkoutInit = {
        body: {
          sub_id: user.attributes["custom:stripe_sub_id"],
        },
      };
      try {
        let response = await API.post(
          "stripe",
          "/get-subscription",
          checkoutInit
        );
        console.log(response);
        this.plan = response.result.plan;
      } catch (err) {
        console.log(err);
      }
    }
  },
};
</script>
